import { createConsumer } from '@rails/actioncable'
import type * as ActionCable from '@rails/actioncable'

import {
  activeConnections,
  consumerCache,
  connectionCleanups,
  eventHandlers,
  socketConfigs,
  type WSPayload,
} from './socketInternalState'
import { trackWsEvent } from '../debug/wsDebug'
import { cleanPath, shouldSocketBeActive, type FullSocketConfig } from '../lib/socketMatcher'
import { getPrimeMode } from '@lib/primeMode'

const defaultChannelForName = (name: string) => `${name.charAt(0).toUpperCase()}${name.slice(1)}Channel`

type SubscriptionIdentifier = ActionCable.ChannelNameWithParams | string | Record<string, unknown>

const resolveSubscriptionIdentifier = (cfg: FullSocketConfig, path: string): SubscriptionIdentifier => {
  if (typeof cfg.subscription === 'function') return cfg.subscription(path)
  if (cfg.subscription) return cfg.subscription

  return { channel: defaultChannelForName(cfg.name) }
}

const PRIME_AWARE_CHANNELS = new Set([
  'Admin::Payouts::IndexChannel',
  'Admin::Payouts::ShowChannel',
  'Admin::Users::IndexChannel',
  'Admin::Users::ShowChannel',
])

/**
 * Returns true if the resolved subscription identifier targets a prime-aware channel.
 */
export const isPrimeAwareConfig = (cfg: FullSocketConfig, path: string): boolean => {
  try {
    const identifier = resolveSubscriptionIdentifier(cfg, path)
    const channel =
      typeof identifier === 'string' ? identifier : (identifier as ActionCable.ChannelNameWithParams).channel

    return typeof channel === 'string' && PRIME_AWARE_CHANNELS.has(channel)
  } catch {
    return false
  }
}

/**
 * Ensures identifier is an object so `subscription.identifier` is stable JSON.
 * ActionCable treats string identifiers as `{ channel: string }`.
 * `prime: true` is only attached to channels that support it (per backend spec).
 */
const normalizeIdentifier = (identifier: SubscriptionIdentifier): ActionCable.ChannelNameWithParams => {
  const base =
    typeof identifier === 'string' ? { channel: identifier } : (identifier as ActionCable.ChannelNameWithParams)

  const shouldAttachPrime = getPrimeMode() && typeof base.channel === 'string' && PRIME_AWARE_CHANNELS.has(base.channel)

  return shouldAttachPrime ? { ...base, prime: true } : base
}

const identifierKey = (identifier: ActionCable.ChannelNameWithParams) => JSON.stringify(identifier)

const setOnConnectCleanup = (name: string, cleanup: (() => void) | undefined) => {
  if (cleanup) {
    connectionCleanups.set(name, cleanup)
  } else {
    connectionCleanups.delete(name)
  }
}

const runAndClearOnConnectCleanup = (name: string) => {
  const cleanup = connectionCleanups.get(name)
  if (!cleanup) return

  try {
    cleanup()
  } catch (e) {
    console.error(`onConnect cleanup error for ${name}`, e)
  } finally {
    connectionCleanups.delete(name)
  }
}

/**
 * Normalizes the WebSocket URL for a given config.
 */
const getWebSocketUrl = (cfg: FullSocketConfig): string => {
  const base = (cfg.url ?? '').replace(/\/$/, '') || window.location.origin

  return base.endsWith('/cable') ? base : `${base}/cable`
}

/**
 * Decrements the consumer ref count and disconnects if no longer used.
 */
const releaseConsumer = (wsUrl: string, consumer: ActionCable.Consumer) => {
  const cached = consumerCache.get(wsUrl)
  if (!cached) return

  cached.refCount = Math.max(0, cached.refCount - 1)
  if (cached.refCount <= 0) {
    try {
      consumer.disconnect()
    } catch (err) {
      console.debug('disconnect failed', err)
    }
    consumerCache.delete(wsUrl)
  }
}

/**
 * Creates the `received` callback for a subscription.
 */
const createReceivedHandler = (cfgName: string) => (data: WSPayload) => {
  trackWsEvent('received', cfgName, data?.event)
  const socketHandlers = eventHandlers.get(cfgName)
  if (!socketHandlers) return

  for (const [eventName, handlerList] of socketHandlers.entries()) {
    const isMatch = eventName === '*' || data.event === eventName
    if (isMatch) {
      handlerList.forEach((handler) => {
        try {
          handler(data)
        } catch (err) {
          console.error('handler error', err)
        }
      })
    }
  }
}

/**
 * Gets or creates an ActionCable consumer for a given URL.
 */
const acquireConsumer = (wsUrl: string): ActionCable.Consumer => {
  const cached = consumerCache.get(wsUrl)
  if (cached) {
    cached.refCount += 1

    return cached.consumer
  }

  const consumer = createConsumer(wsUrl) as ActionCable.Consumer
  consumerCache.set(wsUrl, { consumer, refCount: 1 })

  return consumer
}

/**
 * Re-runs onConnect cleanup and callback if necessary.
 */
const refreshOnConnect = (cfg: FullSocketConfig, subscription: ActionCable.Subscription, path: string) => {
  runAndClearOnConnectCleanup(cfg.name)

  if (!cfg.onConnect) return

  try {
    setOnConnectCleanup(cfg.name, cfg.onConnect(subscription, path))
  } catch (e) {
    console.error(`Error re-running onConnect for ${cfg.name}:`, e)
  }
}

/**
 * Subscribes to a channel; the config's onConnect hook runs with `path` each time the subscription connects.
 */
const subscribe = (
  consumer: ActionCable.Consumer,
  cfg: FullSocketConfig,
  identifier: ActionCable.ChannelNameWithParams,
  path: string,
) => {
  const subscription = consumer.subscriptions.create(identifier, {
    connected() {
      if (!cfg.onConnect) return

      try {
        setOnConnectCleanup(cfg.name, cfg.onConnect(subscription, path))
      } catch (e) {
        console.error(`Error in onConnect for ${cfg.name}:`, e)
      }
    },
    received: createReceivedHandler(cfg.name),
  })

  return subscription
}

/**
 * Disconnects a specific socket by name.
 */
export const disconnectSocket = (name: string) => {
  const entry = activeConnections.get(name)
  if (!entry) return

  const { consumer, subscription, wsUrl } = entry

  // 1. Cleanup onConnect hook
  runAndClearOnConnectCleanup(name)

  // 2. Unsubscribe
  try {
    subscription.unsubscribe()
  } catch (err) {
    console.debug('unsubscribe failed', err)
  }

  // 3. Handle consumer reference counting
  releaseConsumer(wsUrl, consumer)

  // 4. Forget the connection
  activeConnections.delete(name)
  eventHandlers.delete(name)
}

/**
 * Replaces the subscription for an existing socket (e.g. when `/users/123` -> `/users/456`).
 * Keeps the underlying consumer connection.
 */
const replaceSubscription = (cfg: FullSocketConfig, path: string) => {
  const entry = activeConnections.get(cfg.name)
  if (!entry) return

  runAndClearOnConnectCleanup(cfg.name)

  try {
    entry.subscription.unsubscribe()
  } catch (err) {
    console.debug('unsubscribe failed', err)
  }

  let identifier: ActionCable.ChannelNameWithParams
  try {
    identifier = normalizeIdentifier(resolveSubscriptionIdentifier(cfg, path))
  } catch (e) {
    console.error(`Error computing subscription identifier for ${cfg.name}:`, e)

    return
  }

  const subscription = subscribe(entry.consumer, cfg, identifier, path)

  activeConnections.set(cfg.name, {
    ...entry,
    subscription,
    identifier: subscription.identifier,
    path,
  })
}

/**
 * Connects a socket based on its configuration and current path.
 */
export const connectSocket = (cfg: FullSocketConfig, path: string) => {
  if (activeConnections.has(cfg.name)) return

  const wsUrl = getWebSocketUrl(cfg)
  const consumer = acquireConsumer(wsUrl)

  let identifier: ActionCable.ChannelNameWithParams
  try {
    identifier = normalizeIdentifier(resolveSubscriptionIdentifier(cfg, path))
  } catch (e) {
    console.error(`Error computing subscription identifier for ${cfg.name}:`, e)
    releaseConsumer(wsUrl, consumer)

    return
  }

  const subscription = subscribe(consumer, cfg, identifier, path)

  activeConnections.set(cfg.name, {
    consumer,
    subscription,
    wsUrl,
    identifier: subscription.identifier,
    path,
  })
}

/**
 * Orchestrates connections based on browser route changes.
 */
export const handleBrowserRouteChange = (rawPath: string) => {
  const path = cleanPath(rawPath)
  if (!path) return

  socketConfigs.forEach((cfg) => {
    const isActive = shouldSocketBeActive(cfg, path)
    const entry = activeConnections.get(cfg.name)

    if (isActive && !entry) {
      connectSocket(cfg, path)
    } else if (isActive && entry) {
      let desiredKey: string | null = null
      try {
        desiredKey = identifierKey(normalizeIdentifier(resolveSubscriptionIdentifier(cfg, path)))
      } catch (e) {
        console.error(`Error computing desired identifier for ${cfg.name}:`, e)
      }

      // If identifier changes (dynamic route params), resubscribe.
      if (desiredKey && desiredKey !== entry.identifier) {
        replaceSubscription(cfg, path)

        return
      }

      // Avoid re-running onConnect if we received the same cleaned path again.
      if (entry.path !== path) {
        refreshOnConnect(cfg, entry.subscription, path)
        activeConnections.set(cfg.name, { ...entry, path })
      }
    } else if (!isActive && entry) {
      disconnectSocket(cfg.name)
    }
  })
}
